import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import trashCanIcon from '@/shared/assets/trashcan.svg?url';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { OfferBuilderAction, useOfferBuilderContext } from './OfferBuilderContext';
import { useOfferReviewLoadingContext } from './OfferReview/OfferReviewLoadingContext';
import useEnablePricing from './useEnablePricing';
import { hasNameYourRate } from './utils';

export type RemoveItemModalProps =
  | {
      open: false;
      mode: undefined;
      buyer: undefined;
      takerMarket: undefined;
      onClose: () => void;
    }
  | {
      open: true;
      mode: 'buyer';
      buyer: BuyerGroupWithStats;
      onClose: () => void;
    }
  | {
      open: true;
      mode: 'takerMarket';
      takerMarket: TakerMarket;
      onClose: () => void;
    };

const RemoveItemModal = (props: RemoveItemModalProps) => {
  const { open, mode, onClose } = props;
  const enablePricing = useEnablePricing();
  const { dispatch } = useOfferBuilderContext();
  const { selectedCurrency } = useCurrencyContext();
  const buyer = 'buyer' in props ? props.buyer : undefined;
  const takerMarket = 'takerMarket' in props ? props.takerMarket : undefined;
  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();
  const { startTransition } = useOfferReviewLoadingContext();

  const handleRemoveFromOffer = () => {
    const handleDispatch = (action: OfferBuilderAction, shouldUseTransition: boolean) => {
      if (shouldUseTransition) {
        startTransition(() => {
          dispatch(action);
        });
      } else {
        dispatch(action);
      }
    };

    if (mode === 'buyer' && buyer) {
      // Remove selected buyer from offer
      const needsTransition = enablePricing && hasNameYourRate(buyer.takerMarkets);
      handleDispatch({ type: 'TOGGLE_TAKER_MARKETS', payload: buyer.takerMarkets }, needsTransition);
    } else if (mode === 'takerMarket' && takerMarket) {
      // Remove selected taker market from offer
      const needsTransition = enablePricing && takerMarket.marketPricingType === 'PRICE_DISCOVERY';
      handleDispatch({ type: 'TOGGLE_TAKER_MARKET', payload: takerMarket }, needsTransition);
    }

    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // Logic to determine buyer or taker market name and eligible invoice amount
  const config =
    mode === 'takerMarket' && takerMarket
      ? {
          divisionOrBuyerName: getTakerMarketDivisionTitle(takerMarket)?.title || getTakerMarketName(takerMarket),
          eligibleInvoiceAmount: asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency),
        }
      : mode === 'buyer' && buyer
      ? {
          divisionOrBuyerName: buyer.makerOrganizationName,
          eligibleInvoiceAmount: asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency),
        }
      : undefined;

  // Render modal if required data is available
  return config ? (
    <Modal open={open} onClose={handleClose} size="sm">
      <ModalTitleInfo>
        {t('builder.removeItemModalTitle', { divisionOrBuyerName: config.divisionOrBuyerName })}
      </ModalTitleInfo>
      <ModalContent className="py-0">
        <div className="mb-4 mt-2 flex justify-center">
          <img src={trashCanIcon} alt="trash can" className="h-16 w-16 shrink-0" />
        </div>
        <div className="items-start space-y-4 pb-6 text-left">
          <p>{t('builder.removeItemModalWarning', { divisionOrBuyerName: config.divisionOrBuyerName })}</p>
          <p className="font-bold">
            {t('builder.removeItemModalDescription', { eligibleInvoiceAmount: config.eligibleInvoiceAmount })}
          </p>
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={handleClose} variant="secondary">
          {t('core.cancel')}
        </Button>
        <Button variant="destructive" onClick={handleRemoveFromOffer}>
          {t('builder.removeItemModalButton')}
        </Button>
      </ModalActions>
    </Modal>
  ) : null;
};

export default RemoveItemModal;
